import { useRestaurantsService } from '@restworld/data-services';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';

export const useCaptureUtmSource = ({ restaurantId }: { restaurantId?: string }) => {
  const router = useRouter();
  const { query, isReady } = router;
  const { utm_source, ambassador_code } = query;
  const restaurantService = useRestaurantsService();
  const loggedRestaurantId = useRef<string | undefined>(undefined);

  const logRestaurantView = useCallback(async () => {
    // Both inputs arrive after the first render, and firing without them is not a no-op.
    // `restaurantDetailsAtom` defaults to `{}` and is only filled by the `useHydrateAtoms`
    // call further down the showcase component, so `restaurantId` is undefined on mount:
    // axios drops the undefined key and POSTs `{"utm_source": null}`, which the API
    // rejects with `restaurant_id: can't be blank`. `router.query` is likewise empty
    // until the route is ready, so a view logged before that carries no utm_source or
    // ambassador_code even when the URL had them.
    if (!isReady || !restaurantId) return;

    // `useRestaurantsService` is memoized on [baseUrl, jwt], so its identity changes when
    // a token arrives mid-session and re-runs this effect for a restaurant already logged.
    // One view per restaurant per mount. The id is claimed before the request rather than
    // after it, so a re-run while that request is still in flight cannot duplicate the view.
    if (loggedRestaurantId.current === restaurantId) return;
    loggedRestaurantId.current = restaurantId;

    try {
      await restaurantService.createRestaurantViewLog({
        restaurantId,
        utmSource: (utm_source as string) || null,
        ambassadorCode: ambassador_code as string,
      });
    } catch (error) {
      // Release the claim so a later run can retry. Without this a network blip or a
      // transient 5xx would be indistinguishable from a view already logged, and the
      // guard above would drop the view for the rest of the mount.
      loggedRestaurantId.current = undefined;
      console.error('Failed to log restaurant view:', error);
    }
  }, [ambassador_code, isReady, restaurantId, restaurantService, utm_source]);

  useEffect(() => {
    logRestaurantView();
  }, [logRestaurantView]);
};
